import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { 
  MessageCircle, Send, Crown, Lock, Mail, Phone, Clock, 
  ChevronDown, ChevronUp, CheckCircle, AlertCircle, Loader,
  X, Plus, Headphones
} from 'lucide-react';
import { toast } from 'sonner';



const PrioritySupport = ({ user }) => {
  const [supportStatus, setSupportStatus] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  
  const [newTicket, setNewTicket] = useState({
    subject: '',
    message: '',
    category: 'general'
  });
  const [submitting, setSubmitting] = useState(false);
  
  const isPro = user?.subscription_tier === 'pro';
  const isPremium = user?.subscription_tier === 'premium' || isPro;
  
  useEffect(() => {
    loadSupportData();
  }, []);
  
  const loadSupportData = async () => {
    try {
      setLoading(true);
      const [statusRes, ticketsRes] = await Promise.all([
        api.get(`/api/support/status`),
        api.get(`/api/support/tickets`)
      ]);
      setSupportStatus(statusRes.data);
      setTickets(ticketsRes.data.tickets || []);
      
      // Load chat history for Pro users
      if (statusRes.data.is_pro) {
        try {
          const chatRes = await api.get(`/api/support/chat/history`);
          setChatHistory(chatRes.data.sessions || []);
        } catch (e) {
          // Chat history might not exist yet
        }
      }
    } catch (error) {
      console.error('Error loading support data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const submitTicket = async () => {
    if (!newTicket.subject || !newTicket.message) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post(
        `/api/support/tickets`,
        newTicket
      );
      
      setTickets([response.data, ...tickets]);
      setNewTicket({ subject: '', message: '', category: 'general' });
      setShowNewTicket(false);
      toast.success(`Ticket ${response.data.ticket_id} created! Expected response: ${response.data.estimated_response}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };
  
  const sendChatMessage = async () => {
    if (!chatMessage.trim() || !isPro) return;
    
    setSendingChat(true);
    try {
      const response = await api.post(
        `/api/support/chat/message`,
        null,
        { 
          params: { message: chatMessage }
        }
      );
      
      setChatMessage('');
      toast.success('Message sent! A specialist will respond shortly.');
      loadSupportData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingChat(false);
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-500/20 text-blue-400';
      case 'in_progress': return 'bg-yellow-500/20 text-yellow-400';
      case 'closed': return 'bg-green-500/20 text-green-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };
  
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500/20 text-red-400';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };
  
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 flex items-center justify-center">
        <Loader className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div data-testid="priority-support" className="space-y-6">
      {/* Support Status Header */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${isPro ? 'bg-purple-500/20' : 'bg-primary/10'}`}>
              <Headphones className={`h-6 w-6 ${isPro ? 'text-purple-400' : 'text-primary'}`} />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
                {isPro ? 'Priority Support' : 'Support'}
                {isPro && <Crown className="h-4 w-4 text-purple-400" />}
              </h3>
              <p className="text-sm text-muted-foreground">
                {supportStatus?.response_time_sla} response time
              </p>
            </div>
          </div>
          
          {isPro && (
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs font-medium">
                Pro Priority
              </span>
            </div>
          )}
        </div>
        
        {/* Support Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Email Support */}
          <div className="p-4 bg-muted/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-4 w-4 text-blue-400" />
              <span className="text-sm font-medium">Email Support</span>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              {supportStatus?.support_email}
            </p>
            <button
              onClick={() => setShowNewTicket(true)}
              className="w-full px-3 py-2 bg-primary/10 text-primary rounded-lg text-sm hover:bg-primary/20 transition-colors"
            >
              Create Ticket
            </button>
          </div>
          
          {/* Live Chat - Pro Only */}
          <div className={`p-4 rounded-lg ${isPro ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-muted/20'}`}>
            <div className="flex items-center gap-2 mb-2">
              <MessageCircle className={`h-4 w-4 ${isPro ? 'text-purple-400' : 'text-muted-foreground'}`} />
              <span className="text-sm font-medium">Live Chat</span>
              {!isPro && <Lock className="h-3 w-3 text-muted-foreground" />}
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              {isPro ? 'Instant priority messaging' : 'Pro feature'}
            </p>
            {isPro ? (
              <button
                onClick={() => setShowChat(true)}
                className="w-full px-3 py-2 bg-purple-500 text-white rounded-lg text-sm hover:bg-purple-600 transition-colors"
              >
                Start Chat
              </button>
            ) : (
              <a
                href="/dashboard?tab=settings"
                className="block w-full px-3 py-2 bg-muted text-muted-foreground rounded-lg text-sm text-center"
              >
                Upgrade to Pro
              </a>
            )}
          </div>
          
          {/* Phone Callback - Pro Only */}
          <div className={`p-4 rounded-lg ${isPro ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-muted/20'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Phone className={`h-4 w-4 ${isPro ? 'text-purple-400' : 'text-muted-foreground'}`} />
              <span className="text-sm font-medium">Phone Callback</span>
              {!isPro && <Lock className="h-3 w-3 text-muted-foreground" />}
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              {isPro ? 'Request a callback' : 'Pro feature'}
            </p>
            {isPro ? (
              <button
                onClick={() => toast.info('Phone callback feature coming soon!')}
                className="w-full px-3 py-2 bg-purple-500/20 text-purple-400 rounded-lg text-sm hover:bg-purple-500/30 transition-colors"
              >
                Request Callback
              </button>
            ) : (
              <a
                href="/dashboard?tab=settings"
                className="block w-full px-3 py-2 bg-muted text-muted-foreground rounded-lg text-sm text-center"
              >
                Upgrade to Pro
              </a>
            )}
          </div>
        </div>
        
        {/* SLA Info */}
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Response SLA:</span>
              <span className={`font-medium ${isPro ? 'text-purple-400' : ''}`}>
                {supportStatus?.response_time_sla}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Open Tickets:</span>
              <span className="font-medium">{supportStatus?.open_tickets || 0}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Ticket History */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold">Support Tickets</h4>
          <button
            onClick={() => setShowNewTicket(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Ticket
          </button>
        </div>
        
        {tickets.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No support tickets yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <div key={ticket.ticket_id} className="p-4 bg-muted/20 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-muted-foreground">{ticket.ticket_id}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(ticket.status)}`}>
                        {ticket.status}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                    </div>
                    <h5 className="font-medium">{ticket.subject}</h5>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(ticket.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">{ticket.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* New Ticket Modal */}
      {showNewTicket && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Create Support Ticket</h3>
              <button onClick={() => setShowNewTicket(false)} className="p-1 hover:bg-muted rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {isPro && (
              <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg flex items-center gap-2">
                <Crown className="h-4 w-4 text-purple-400" />
                <span className="text-sm text-purple-400">Priority support - 4 hour response guarantee</span>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  value={newTicket.category}
                  onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                >
                  <option value="general">General Question</option>
                  <option value="technical">Technical Issue</option>
                  <option value="billing">Billing & Subscription</option>
                  <option value="feature_request">Feature Request</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Subject</label>
                <input
                  type="text"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                  placeholder="Brief description of your issue"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Message</label>
                <textarea
                  value={newTicket.message}
                  onChange={(e) => setNewTicket({...newTicket, message: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg resize-none"
                  placeholder="Please describe your issue in detail..."
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowNewTicket(false)}
                  className="flex-1 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitTicket}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {submitting ? <Loader className="h-4 w-4 animate-spin mx-auto" /> : 'Submit Ticket'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Live Chat Modal - Pro Only */}
      {showChat && isPro && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-purple-500/30 rounded-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-purple-400" />
                <span className="font-semibold">Priority Live Chat</span>
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full text-xs">Online</span>
              </div>
              <button onClick={() => setShowChat(false)} className="p-1 hover:bg-muted rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="h-80 p-4 overflow-y-auto space-y-3">
              <div className="p-3 bg-purple-500/10 rounded-lg max-w-[80%]">
                <p className="text-sm">Welcome to Priority Support! How can we help you today?</p>
                <span className="text-xs text-muted-foreground">Support Team • Just now</span>
              </div>
              
              {chatHistory[0]?.messages?.map((msg, idx) => (
                <div 
                  key={idx} 
                  className={`p-3 rounded-lg max-w-[80%] ${
                    msg.from === 'user' 
                      ? 'bg-primary/20 ml-auto' 
                      : 'bg-muted/30'
                  }`}
                >
                  <p className="text-sm">{msg.message}</p>
                  <span className="text-xs text-muted-foreground">
                    {msg.from === 'user' ? 'You' : 'Support'} • {new Date(msg.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
            
            <div className="p-4 border-t border-border">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                  placeholder="Type your message..."
                  className="flex-1 px-3 py-2 bg-background border border-border rounded-lg"
                />
                <button
                  onClick={sendChatMessage}
                  disabled={sendingChat || !chatMessage.trim()}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50"
                >
                  {sendingChat ? <Loader className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Priority support • Typical response: 4 hours
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrioritySupport;
